<template>
  <div class="user-list">
    <div class="d-flex justify-content-between align-items-center mb-4">
      <h2 class="mb-0">{{ $t("pre_checkin.booking") }}</h2>
      <router-link :to="{ name: 'BookingPreCheckin' }" class="btn btn-primary">
        <b-icon icon="plus" class="icon-style"></b-icon>
        {{ $t("pre_checkin.booking_title") }}
      </router-link>
    </div>

    <CrudTable
      :columns="fields"
      :data-sources="bookings"
      :loader="isLoading"
      list-url="/bookings"
      ref="action-table"
    >
      <template #cell(guest_info)="data">
        <div class="guest-info">
          <div class="guest-name">{{ data.item.user?.full_name }}</div>
          <div class="guest-email text-muted small">
            {{ data.item.user?.email }}
          </div>
        </div>
      </template>

      <template #cell(hotel_info)="data">
        <div v-if="data.item.hotel" class="hotel-info">
          <div class="hotel-name">{{ data.item.hotel.name }}</div>
          <div class="hotel-location text-muted small">
            {{ data.item.hotel.address }}
          </div>
        </div>
        <div v-else class="text-muted">
          {{ $t("pre_checkin.no_hotel_info") }}
        </div>
      </template>

      <template #cell(check_in)="data">
        <div class="dates-info">
          <div class="check-in">
            <i class="fas fa-sign-in-alt text-success"></i>
            {{ data.item.check_in }}
          </div>
          <div class="check-out">
            <i class="fas fa-sign-out-alt text-danger"></i>
            {{ data.item.check_out }}
          </div>
        </div>
      </template>

      <template #cell(room_info)="data">
        <div class="room-info">
          <div class="room-count">
            <i class="fas fa-bed text-primary"></i>
            {{ data.item.num_of_rooms }}
            {{ data.item.num_of_rooms > 1 ? $t("booking.rooms") : $t("booking.room") }}
          </div>
          <div class="guest-count text-muted small">
            {{ data.item.num_of_adults }}
            {{ $t("booking.adults") }},
            {{ data.item.num_of_child }}
            {{ $t("booking.children") }}
          </div>
        </div>
      </template>

      <template #cell(payment_info)="data">
        <div
          class="payment-info"
          v-b-tooltip.hover.html="getPaymentSummaryTooltip(data.item.billings)"
          style="cursor: pointer; display: flex; align-items: center; gap: 6px"
        >
          <!-- Total Amount -->
          <div class="final-price" style="font-weight: bold; color: #28a745">
            {{ formatCurrency(getNetBillingAmount(data.item.billings)) }}
          </div>

          <!-- Tooltip Icon -->
          <i class="fas fa-info-circle" style="color: #6c757d; font-size: 14px"></i>

          <!-- Payment Status -->
          <div class="payment-status">
            <span class="badge" :class="getPaymentStatusClass(data.item.payment_status)">
              {{ data.item.payment_status }}
            </span>
          </div>
        </div>
      </template>

      <template #cell(reservation_status)="data">
        <span
          class="badge"
          :class="getReservationStatusClass(data.item.reservation_status)"
        >
          {{ data.item.reservation_status }}
        </span>
        <!-- Status Info for Completed Actions -->
        <div
          v-if="
            data.item.pre_checkin?.offer_id &&
            !canProcessRefund(data.item) &&
            !canCancelOffer(data.item)
          "
          class="text-muted small mb-1"
        >
          <span
            v-if="data.item.pre_checkin.refund_status === 'processed'"
            class="badge bg-primary text-white"
          >
            <i class="fas fa-check"></i> Refund Processed
          </span>
          <span
            v-else-if="data.item.pre_checkin.refund_status === 'cancelled'"
            class="badge bg-danger text-white"
          >
            <i class="fas fa-times"></i> Offer Cancelled
          </span>
        </div>
      </template>

      <template #cell(precheckin_sent)="data">
        <span
          class="badge px-3 py-1 rounded-pill"
          :class="getPrecheckinBadgeClass(data.item.precheckin_sent)"
        >
          {{ getPrecheckinLabel(data.item.precheckin_sent) }}
        </span>
      </template>

      <template #cell(actions)="data">
        <div class="d-flex gap-2">
          <!-- View Details Button -->
          <b-button
            variant="info"
            size="sm"
            @click="viewBookingDetails(data.item)"
            title="View Booking Details"
          >
            <i class="fas fa-eye"></i>
            {{ $t("common.view") }}
          </b-button>

          <!-- If booking is not confirmed, show only Confirm button -->
          <b-button
            v-if="
              data.item.hotel.booking_confirmation === 'manual' &&
              !data.item.confirmation_sent
            "
            variant="success"
            size="sm"
            @click="confirmBooking(data.item)"
            title="Confirm Booking"
          >
            <i class="fas fa-check"></i>
            Confirm & Send PDF
          </b-button>

          <!-- If booking is confirmed, show Change/Upgrade Room and Pre-checkin actions -->
          <template v-else>
            <!-- Change/Upgrade Room Button -->
            <b-button
              v-if="!['checked_in', 'completed'].includes(data.item.reservation_status)"
              variant="warning"
              size="sm"
              @click="openRoomChangeModal(data.item)"
              title="Change or Upgrade Room"
            >
              <i class="fas fa-exchange-alt"></i>
              Change/Upgrade Room
            </b-button>

            <!-- ✅ Check In Button for Receptionist -->
            <b-button
              v-if="
                data.item.reservation_status !== 'checked_in' &&
                data.item.reservation_status !== 'completed'
              "
              variant="success"
              size="sm"
              @click="markAsCheckedIn(data.item)"
              title="Check In Booking"
            >
              <i class="fas fa-door-open"></i>
              {{ $t("pre_checkin.check_in") }}
            </b-button>
            <!-- Send Pre-checkin Button -->
            <b-button
              v-if="data.item.precheckin_sent === 0"
              variant="primary"
              size="sm"
              @click="sendPrecheckin(data.item)"
              :disabled="
                !isSendPrecheckinEnabled(data.item.check_in) ||
                data.item.precheckin_sent === 1
              "
              :title="
                !isSendPrecheckinEnabled(data.item.check_in)
                  ? $t('pre_checkin.not_within_72hrs_tooltip')
                  : ''
              "
            >
              <i class="fas fa-paper-plane"></i>
              {{ $t("pre_checkin.send_precheckin") }}
            </b-button>

            <!-- View Pre-checkin Details Button -->
            <b-button
              v-if="data.item.precheckin_sent === 2"
              size="sm"
              @click="viewPrecheckinDetails(data.item)"
              title="View Pre-checkin Details"
              style="background-color: yellow; border-color: yellow; color: black"
            >
              <i class="fas fa-clipboard-check"></i>
              {{ $t("pre_checkin.view_details") }}
            </b-button>
            <!-- Open Pre-checkin Form (Admin) -->
            <b-button
              v-if="data.item.precheckin_sent >= 1 && data.item.precheckin_link"
              variant="info"
              size="sm"
              :href="data.item.precheckin_link"
              target="_blank"
              title="Open Pre-checkin form"
            >
              <i class="fas fa-file-alt"></i>
            </b-button>

            <b-button
              v-if="
                data.item.reservation_status === 'checked_in' &&
                data.item.payment_status != 'paid'
              "
              variant="dark"
              size="sm"
              @click="openCashPaymentModal(data.item)"
            >
              <i class="fas fa-hand-holding-usd"></i>
              Add Payment
            </b-button>
            <!-- Checkout Button -->
            <b-button
              v-if="
                data.item.reservation_status === 'checked_in' &&
                data.item.payment_status === 'paid'
              "
              variant="warning"
              size="sm"
              @click="checkoutBooking(data.item)"
              :disabled="isLoading"
            >
              <i class="fas fa-sign-out-alt"></i>
              {{ $t("pre_checkin.checkout") }}
            </b-button>
            <b-button
              v-if="!['completed'].includes(data.item.reservation_status)"
              variant="success"
              size="sm"
              @click="goToPreCheckinChat(data.item)"
              title="Go to Pre-checkin Chat"
            >
              <i class="fas fa-comments"></i>
            </b-button>
            <!-- Generate Invoice Button -->
            <b-button
              v-if="
                ['checked_in'].includes(data.item.reservation_status) &&
                data.item.payment_status === 'paid'
              "
              variant="secondary"
              size="sm"
              @click="generateCheckoutBill(data.item)"
              title="Generate Invoice"
            >
              <i class="fas fa-file-invoice-dollar"></i>
              Invoice
            </b-button>

            <!-- View Invoice Button -->
            <b-button
              v-if="
                data.item.invoice_generated == true &&
                ['completed'].includes(data.item.reservation_status)
              "
              size="sm"
              :href="data.item.invoice_url"
              target="_blank"
              title="View Invoice"
              style="background-color: #ff9800; border-color: #ff9800; color: white"
            >
              <i class="fas fa-file-invoice"></i>
            </b-button>

            <!-- Refund Management Buttons -->
            <div
              v-if="
                data.item.reservation_status == 'checked_in' &&
                data.item.payment_method === 'stripe'
              "
              class="d-flex gap-1"
            >
              <!-- Process Refund Button -->
              <b-button
                v-if="canProcessRefund(data.item)"
                variant="success"
                size="sm"
                @click="processRefund(data.item)"
                :title="$t('pre_checkin_offers.process_refund')"
              >
                <i class="fas fa-money-bill-wave"></i>
                Refund
              </b-button>

              <!-- Mark Refund Pending Button -->
              <!-- <b-button v-if="canProcessRefund(data.item)" variant="warning" size="sm"
                                @click="markRefundPending(data.item)" :title="$t('pre_checkin_offers.mark_pending')">
                                <i class="fas fa-clock"></i>
                                Pending
                            </b-button> -->

              <!-- Cancel Offer Button -->
              <b-button
                v-if="canCancelOffer(data.item)"
                variant="danger"
                size="sm"
                @click="cancelOffer(data.item)"
                :title="$t('pre_checkin_offers.cancel_offer')"
              >
                <i class="fas fa-times"></i>
                Cancel
              </b-button>
            </div>
          </template>
        </div>
      </template>
    </CrudTable>

    <!-- Booking Details Modal -->
    <b-modal
      v-model="showBookingModal"
      :title="$t('booking.details')"
      size="lg"
      scrollable
    >
      <div v-if="selectedBooking" class="booking-details">
        <!-- Guest Information -->
        <div class="section mb-4">
          <h5 class="section-title">
            <i class="fas fa-user text-primary"></i>
            {{ $t("booking.guest_information") }}
          </h5>
          <div class="row">
            <div class="col-md-6">
              <p>
                <strong>{{ $t("booking.name") }}:</strong>
                {{ selectedBooking.user?.full_name }}
              </p>
              <p>
                <strong>{{ $t("booking.email") }}:</strong>
                {{ selectedBooking.user?.email }}
              </p>
              <p>
                <strong>{{ $t("booking.phone") }}:</strong>
                {{ selectedBooking.user?.phone }}
              </p>
            </div>
            <div class="col-md-6">
              <p>
                <strong>{{ $t("booking.adults") }}:</strong>
                {{ selectedBooking.num_of_adults }}
              </p>
              <p>
                <strong>{{ $t("booking.children") }}:</strong>
                {{ selectedBooking.num_of_child }}
              </p>
            </div>
          </div>
          <div v-if="selectedBooking.special_request" class="mt-2">
            <p>
              <strong>{{ $t("booking.special_requests") }}:</strong>
            </p>
            <p class="text-muted">
              {{ selectedBooking.special_request }}
            </p>
          </div>
        </div>

        <!-- Hotel Information -->
        <div class="section mb-4" v-if="selectedBooking.hotel">
          <h5 class="section-title">
            <i class="fas fa-hotel text-info"></i>
            {{ $t("booking.hotel_information") }}
          </h5>
          <div class="row">
            <div class="col-md-6">
              <p>
                <strong>{{ $t("booking.hotel_name") }}:</strong>
                {{ selectedBooking.hotel.name }}
              </p>
              <p>
                <strong>{{ $t("booking.address") }}:</strong>
                {{ selectedBooking.hotel.address }}
              </p>
            </div>
            <div class="col-md-6">
              <p>
                <strong>{{ $t("booking.phone") }}:</strong>
                {{ selectedBooking.hotel.phone }}
              </p>
              <p>
                <strong>{{ $t("booking.rating") }}:</strong>
                {{ selectedBooking.hotel.rating }}/5
              </p>
            </div>
          </div>
        </div>

        <!-- Booking Details -->
        <div class="section mb-4">
          <h5 class="section-title">
            <i class="fas fa-calendar-alt text-success"></i>
            {{ $t("booking.booking_details") }}
          </h5>
          <div class="row">
            <div class="col-md-6">
              <p>
                <strong>{{ $t("booking.check_in") }}:</strong>
                {{ selectedBooking.check_in }}
              </p>
              <p>
                <strong>{{ $t("booking.check_out") }}:</strong>
                {{ selectedBooking.check_out }}
              </p>
              <p>
                <strong>{{ $t("booking.rooms") }}:</strong>
                {{ selectedBooking.num_of_rooms }}
              </p>
            </div>
            <div class="col-md-6">
              <p>
                <strong>{{ $t("booking.payment_method") }}:</strong>
                {{ selectedBooking.payment_method }}
              </p>
              <p>
                <strong>{{ $t("booking.payment_status") }}:</strong>
                <span
                  class="badge"
                  :class="getPaymentStatusClass(selectedBooking.payment_status)"
                >
                  {{ selectedBooking.payment_status }}
                </span>
              </p>
              <p>
                <strong>{{ $t("booking.reservation_status") }}:</strong>
                <span
                  class="badge"
                  :class="getReservationStatusClass(selectedBooking.reservation_status)"
                >
                  {{ selectedBooking.reservation_status }}
                </span>
              </p>
            </div>
          </div>
        </div>

        <!-- Room and Guest Details -->
        <div
          class="section mb-4"
          v-if="
            selectedBooking.booking_room_types &&
            selectedBooking.booking_room_types.length
          "
        >
          <h5 class="section-title">
            <i class="fas fa-bed text-warning"></i>
            {{ $t("booking.room_details") }}
          </h5>
          <div
            v-for="(roomType, index) in selectedBooking.booking_room_types"
            :key="index"
            class="room-type-section mb-3"
          >
            <div class="room-type-header">
              <h6 class="mb-2">{{ roomType.room_type.name }}</h6>
              <span class="badge bg-primary"
                >{{ roomType.num_of_rooms }}
                {{ roomType.num_of_rooms > 1 ? "Rooms" : "Room" }}</span
              >
            </div>

            <div
              v-if="roomType.booking_room && roomType.booking_room.length"
              class="rooms-list"
            >
              <div
                v-for="(bookingRoom, roomIndex) in roomType.booking_room"
                :key="roomIndex"
                class="room-item p-2 border rounded mb-2"
              >
                <div class="d-flex justify-content-between align-items-start">
                  <div>
                    <strong>{{ $t("booking.room_number") }}:</strong>
                    {{ bookingRoom.room.room_number }}
                    <span class="badge bg-secondary ms-2"
                      >{{ bookingRoom.room.floor }}{{ $t("booking.floor_suffix") }}</span
                    >
                  </div>
                  <div class="text-muted small">
                    {{ bookingRoom.room.view }}
                    {{ $t("booking.view") }}
                  </div>
                </div>

                <!-- Guests in this room -->
                <div
                  v-if="bookingRoom.guests && bookingRoom.guests.length"
                  class="guests-list mt-2"
                >
                  <small class="text-muted">{{ $t("booking.guests") }}:</small>
                  <div
                    v-for="(guest, guestIndex) in bookingRoom.guests"
                    :key="guestIndex"
                    class="guest-item"
                  >
                    <i class="fas fa-user-circle text-primary"></i>
                    {{ guest.name }}
                    <span v-if="guest.date_of_birth" class="text-muted small">
                      ({{ formatDate(guest.date_of_birth) }})
                    </span>
                  </div>
                </div>
                <div v-else class="text-muted small mt-1">
                  <i class="fas fa-info-circle"></i>
                  {{ $t("booking.no_guests_assigned") }}
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Pricing Information -->
        <div class="section mb-4">
          <h5 class="section-title">
            <i class="fas fa-dollar-sign text-success"></i>
            {{ $t("booking.pricing") }}
          </h5>
          <div class="row">
            <div class="col-md-6">
              <p>
                <strong>{{ $t("booking.total_price") }}:</strong>
                {{ selectedBooking.total_price }}
              </p>
              <p v-if="selectedBooking.discount_amount">
                <strong>{{ $t("booking.discount") }}:</strong>
                {{ selectedBooking.discount_amount }}
              </p>
            </div>
            <div class="col-md-6">
              <p>
                <strong>{{ $t("booking.created_at") }}:</strong>
                {{ selectedBooking.created_at }}
              </p>
              <p>
                <strong>{{ $t("booking.updated_at") }}:</strong>
                {{ selectedBooking.updated_at }}
              </p>
            </div>
          </div>
        </div>

        <!-- Pre-checkin Information -->
        <div class="section mb-4">
          <h5 class="section-title">
            <i class="fas fa-clipboard-check text-info"></i>
            {{ $t("booking.pre_checkin_status") }}
          </h5>
          <div class="row">
            <div class="col-md-6">
              <p>
                <strong>{{ $t("booking.status") }}:</strong>
                <span
                  class="badge"
                  :class="getPrecheckinBadgeClass(selectedBooking.precheckin_sent)"
                >
                  {{ getPrecheckinLabel(selectedBooking.precheckin_sent) }}
                </span>
              </p>
            </div>
            <div class="col-md-6" v-if="selectedBooking.precheckin_token">
              <p>
                <strong>{{ $t("booking.token") }}:</strong>
                {{ selectedBooking.precheckin_token }}
              </p>
            </div>
          </div>
        </div>
      </div>
      <template #modal-footer>
        <b-button variant="secondary" @click="showBookingModal = false">
          {{ $t("common.close") }}
        </b-button>
        <b-button
          v-if="selectedBooking && selectedBooking.precheckin_sent === 0"
          variant="primary"
          @click="sendPrecheckin(selectedBooking)"
        >
          <i class="fas fa-paper-plane"></i>
          {{ $t("pre_checkin.send_precheckin") }}
        </b-button>
      </template>
    </b-modal>

    <!-- Pre-checkin Details Modal -->
    <b-modal
      v-model="showPrecheckinModal"
      :title="$t('pre_checkin.details')"
      size="xl"
      scrollable
    >
      <div v-if="selectedPrecheckin">
        <div class="section mb-4">
          <h5 class="section-title">
            <i class="fas fa-hotel text-primary"></i>
            {{ $t("pre_checkin.booking_information") }}
          </h5>
          <div class="row">
            <div class="row">
              <div class="col-md-4">
                <p>
                  <strong>{{ $t("pre_checkin.booking_id") }}:</strong>
                  {{ selectedPrecheckin.id }}
                </p>
              </div>
              <div class="col-md-4">
                <p>
                  <strong>{{ $t("pre_checkin.hotel_name") }}:</strong>
                  {{ selectedPrecheckin.hotel.name }}
                </p>
              </div>
              <div class="col-md-4">
                <p>
                  <strong>{{ $t("pre_checkin.checkin") }}:</strong>
                  {{ selectedPrecheckin.check_in }}
                </p>
              </div>
              <div class="col-md-4">
                <p>
                  <strong>{{ $t("pre_checkin.checkout") }}:</strong>
                  {{ formatDate(selectedPrecheckin.check_out) }}
                </p>
              </div>
              <div class="col-md-4">
                <p>
                  <strong>{{ $t("pre_checkin.room_summary") }}:</strong>
                  {{ roomSummary }}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div class="section mb-4">
          <h5 class="section-title">
            <i class="fas fa-plane-arrival text-dark"></i>
            {{ $t("pre_checkin.arrival_information") }}
          </h5>
          <div class="row">
            <div class="col-md-4">
              <p>
                <strong>{{ $t("pre_checkin.estimated_arrival_time") }}:</strong>
                {{ selectedPrecheckin.pre_checkin.arrival_time }}
              </p>
            </div>
            <div class="col-md-4">
              <p>
                <strong>{{ $t("pre_checkin.mode_of_arrival") }}:</strong>
                {{ selectedPrecheckin.pre_checkin.arrival_mode }}
              </p>
            </div>
            <div class="col-md-4">
              <p>
                <strong>{{ $t("pre_checkin.special_instructions") }}:</strong>
                {{ selectedPrecheckin.pre_checkin.arrival_instructions }}
              </p>
            </div>
          </div>
        </div>

        <div class="section mb-4">
          <h5 class="section-title">
            <i class="fas fa-bed text-warning"></i>
            {{ $t("pre_checkin.preferences") }}
          </h5>
          <div class="row">
            <div class="col-md-4">
              <p>
                <strong>{{ $t("pre_checkin.bed_preference") }}:</strong>
                {{ selectedPrecheckin.pre_checkin.bed_preference }}
              </p>
            </div>
            <div class="col-md-4">
              <p>
                <strong>{{ $t("pre_checkin.smoking_preference") }}:</strong>
                {{ selectedPrecheckin.pre_checkin.smoking_preference }}
              </p>
            </div>
            <div class="col-md-4">
              <p>
                <strong>{{ $t("pre_checkin.checkin_options") }}:</strong>
                <span class="value">
                  {{ $t("pre_checkin.late_checkin") }}:
                  {{
                    selectedPrecheckin.pre_checkin?.late_checkin
                      ? $t("common.yes")
                      : $t("common.no")
                  }}, {{ $t("pre_checkin.early_checkin") }}:
                  {{
                    selectedPrecheckin.pre_checkin?.early_checkin
                      ? $t("common.yes")
                      : $t("common.no")
                  }}
                </span>
              </p>
            </div>
          </div>
        </div>

        <div class="section mb-4">
          <h5 class="section-title">
            <i class="fas fa-user-shield text-danger"></i>
            {{ $t("pre_checkin.emergency_contact") }}
          </h5>
          <div class="row">
            <div class="col-md-4">
              <p>
                <strong>{{ $t("pre_checkin.name") }}:</strong>
                {{ selectedPrecheckin.pre_checkin.emergency_name }}
              </p>
            </div>
            <div class="col-md-4">
              <p>
                <strong>{{ $t("pre_checkin.relationship") }}:</strong>
                {{ selectedPrecheckin.pre_checkin.emergency_relationship }}
              </p>
            </div>
            <div class="col-md-4">
              <p>
                <strong>{{ $t("pre_checkin.phone") }}:</strong>
                {{ selectedPrecheckin.pre_checkin.emergency_phone }}
              </p>
            </div>
            <div class="col-md-4">
              <p>
                <strong>{{ $t("pre_checkin.email") }}:</strong>
                {{ selectedPrecheckin.pre_checkin.emergency_email }}
              </p>
            </div>
          </div>
        </div>

        <!-- <div class="section mb-4">
                    <h5 class="section-title">
                        <i class="fas fa-credit-card text-primary"></i> {{ $t('pre_checkin.payment_information') }}
                    </h5>
                    <div class="row">
                        <div class="col-md-4">
                            <p><strong>{{ $t('pre_checkin.payment_status') }}:</strong> {{ selectedPrecheckin.pre_checkin.payment_status }}</p>
                        </div>
                        <div class="col-md-4">
                            <p><strong>{{ $t('pre_checkin.payment_method') }}:</strong> {{ selectedPrecheckin.pre_checkin.payment_method }}</p>
                        </div>
                        <div class="col-md-4">
                            <p><strong>{{ $t('pre_checkin.amount_paid') }}:</strong> {{ selectedPrecheckin.pre_checkin.amount_paid }}</p>
                        </div>
                        <div class="col-md-4">
                            <p><strong>{{ $t('pre_checkin.balance_due') }}:</strong> {{ selectedPrecheckin.pre_checkin.balance_due }}</p>
                        </div>
                        <div class="col-md-4">
                            <p><strong>{{ $t('pre_checkin.gst_number') }}:</strong> {{ selectedPrecheckin.pre_checkin.gst_number }}</p>
                        </div>
                        <div class="col-md-4">
                            <p><strong>{{ $t('pre_checkin.company_name') }}:</strong> {{ selectedPrecheckin.pre_checkin.company_name }}</p>
                        </div>
                    </div>
                </div> -->

        <div class="section mb-4">
          <h5 class="section-title">
            <i class="fas fa-clipboard-check text-light"></i>
            {{ $t("pre_checkin.declarations_signature") }}
          </h5>
          <div class="row">
            <div class="col-md-4">
              <p>
                <strong>{{ $t("pre_checkin.agreed_to_hotel_policies") }}:</strong>
                {{
                  selectedPrecheckin.pre_checkin.agreed
                    ? $t("common.yes")
                    : $t("common.no")
                }}
              </p>
            </div>
            <div class="col-md-4">
              <p>
                <strong>{{ $t("pre_checkin.confirmed_details_accuracy") }}:</strong>
                {{
                  selectedPrecheckin.pre_checkin.confirmed
                    ? $t("common.yes")
                    : $t("common.no")
                }}
              </p>
            </div>
            <div class="col-md-4">
              <p>
                <strong>{{ $t("pre_checkin.signature") }}:</strong>
              </p>
              <div
                v-if="selectedPrecheckin.pre_checkin.signature_data"
                class="signature-display text-center"
              >
                <img
                  :src="selectedPrecheckin.pre_checkin.signature_data"
                  alt="Guest Signature"
                  class="signature-image shadow"
                />
              </div>
            </div>
          </div>
        </div>

        <div class="section mb-4">
          <h5 class="section-title">
            <i class="fas fa-users text-success"></i>
            {{ $t("pre_checkin.guest_details") }}
          </h5>
          <div class="row">
            <div
              v-for="(roomType, rtIndex) in selectedPrecheckin.booking_room_types"
              :key="roomType.id"
              class="mb-4"
            >
              <h6 class="mb-3 border-bottom pb-1">
                <i class="fas fa-door-open text-primary"></i>
                {{ $t("pre_checkin.room_type") }}
                {{ rtIndex + 1 }}:
                {{ roomType.room_type?.name }}
              </h6>

              <div class="row">
                <div
                  class="col-md-6 mb-4"
                  v-for="(room, roomIndex) in roomType.booking_room"
                  :key="room.id"
                >
                  <div class="border rounded p-3 h-100 shadow-sm bg-light">
                    <h6 class="mb-2">
                      <i class="fas fa-bed text-info"></i>
                      {{ $t("pre_checkin.room") }}
                      {{ roomIndex + 1 }} ({{ $t("pre_checkin.room_number") }}:
                      {{ room.room?.room_number || "N/A" }})
                    </h6>

                    <div class="row">
                      <div
                        class="col-sm-6 mb-3"
                        v-for="guest in room.guests"
                        :key="guest.id"
                      >
                        <div class="card h-100 border-0 shadow-sm">
                          <div class="card-body p-2">
                            <h6 class="card-title mb-1 text-truncate">
                              <i class="fas fa-user text-secondary"></i>
                              {{ guest.name }}
                            </h6>
                            <small
                              ><strong>{{ $t("pre_checkin.dob") }}:</strong>
                              {{ guest.date_of_birth }}</small
                            ><br />
                            <small
                              ><strong>{{ $t("pre_checkin.gender") }}:</strong>
                              {{ guest.gender }}</small
                            ><br />
                            <small
                              ><strong>{{ $t("pre_checkin.passport_id") }}:</strong>
                              {{ guest.passport_number }}</small
                            >

                            <div v-if="guest.passportImageUrl?.length" class="mt-2">
                              <small class="d-block"
                                ><strong
                                  >{{ $t("pre_checkin.passport_image") }}:</strong
                                ></small
                              >
                              <img
                                :src="guest.passportImageUrl[0].path"
                                class="img-fluid border rounded"
                                style="max-height: 100px"
                                alt="Passport"
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <!-- row for guests -->
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <template #modal-footer>
        <b-button variant="secondary" @click="showPrecheckinModal = false">
          {{ $t("common.close") }}
        </b-button>
      </template>
    </b-modal>
    <!-- Room Change Modal -->
    <b-modal
      v-model="showRoomChangeModal"
      title="Change/Upgrade Room"
      size="lg"
      @hide="cancelRoomChange"
    >
      <div v-if="!roomToChange">
        <div
          v-for="roomType in roomChangeBooking?.booking_room_types"
          :key="roomType.id"
          class="mb-4"
        >
          <h5 class="mb-2">{{ roomType.room_type.name }}</h5>
          <ul class="list-group">
            <li
              v-for="room in roomType.booking_room"
              :key="room.id"
              class="list-group-item d-flex justify-content-between align-items-center"
            >
              <div>
                <b-icon icon="door-closed" class="me-2 text-primary" />
                <strong>Room {{ room.room.room_number }}</strong>
                <span class="text-muted ms-2"
                  >({{ room.room.view }}, Floor {{ room.room.floor }})</span
                >
              </div>
              <b-button
                size="sm"
                variant="warning"
                @click="startRoomChange(room, roomType)"
                >Change</b-button
              >
            </li>
          </ul>
        </div>
      </div>

      <!-- Step 2: Room selection UI -->
      <div v-else>
        <b-button variant="link" class="mb-2 p-0" @click="cancelRoomChange">
          <b-icon icon="arrow-left" /> Back to Room List
        </b-button>
        <div class="mb-3 p-2 border rounded bg-light">
          <strong>Current Room:</strong>
          <div>
            <b-icon icon="door-closed" class="me-2 text-primary" />
            <b>Room {{ roomToChange.room.room_number }}</b>
            <span class="text-muted ms-2"
              >({{ roomToChange.room.view }}, Floor {{ roomToChange.room.floor }})</span
            >
          </div>
          <div v-if="roomToChange.room.notes" class="small text-muted mt-1">
            <b-icon icon="info-circle" />
            {{ roomToChange.room.notes }}
          </div>
        </div>
        <div>
          <strong>Select New Room:</strong>
          <div v-if="isLoadingAvailableRooms" class="text-center my-4">
            <b-spinner label="Loading..."></b-spinner>
            <div>Loading available rooms...</div>
          </div>
          <div v-else-if="availableRooms.length" class="row">
            <div
              v-for="room in availableRooms"
              :key="room.id"
              class="col-sm-6 col-md-4 col-lg-3 mb-3"
            >
              <div
                class="room-card card h-100 shadow-sm"
                :class="{
                  'border-primary': selectedNewRoomId === room.id,
                }"
                @click="selectedNewRoomId = room.id"
                style="
                  cursor: pointer;
                  min-width: 180px;
                  max-width: 220px;
                  margin: 0 auto;
                "
              >
                <div class="card-body py-2 px-3">
                  <div class="d-flex align-items-center mb-2">
                    <b-icon icon="door-open" class="me-2 text-success" />
                    <b style="font-size: 1.1rem">Room {{ room.room_number }}</b>
                    <span class="badge bg-info ms-2" style="font-size: 0.8rem">{{
                      room.type || roomTypeToChange.room_type.name
                    }}</span>
                  </div>
                  <ul class="mb-0 small text-muted" style="font-size: 0.9rem">
                    <li><b>View:</b> {{ room.view }}</li>
                    <li><b>Floor:</b> {{ room.floor }}</li>
                    <li v-if="room.size_sqft">
                      <b>Size:</b>
                      {{ room.size_sqft }} sqft
                    </li>
                    <li v-if="room.notes"><b>Notes:</b> {{ room.notes }}</li>
                  </ul>
                </div>
                <div class="card-footer bg-transparent border-0 text-end p-1">
                  <b-form-radio
                    :value="room.id"
                    v-model="selectedNewRoomId"
                    name="roomSelect"
                    class="me-2"
                    style="font-size: 0.9rem"
                    >Select</b-form-radio
                  >
                </div>
              </div>
            </div>
          </div>
          <div v-else class="text-danger">
            No available rooms found for this type and date range.
          </div>
        </div>
        <b-form-input
          v-model="roomChangeReason"
          placeholder="Reason for change (e.g. Upgrade, Guest request)"
          class="mb-2 mt-3"
        ></b-form-input>
        <b-button
          variant="success"
          size="sm"
          :disabled="!selectedNewRoomId"
          @click="submitRoomChange"
          >Confirm Change</b-button
        >
        <b-button variant="secondary" size="sm" class="ms-2" @click="cancelRoomChange"
          >Cancel</b-button
        >
      </div>
    </b-modal>

    <!-- Refund Modal -->
    <b-modal
      v-model="showRefundModal"
      title="Process Refund"
      @ok="handleRefundSubmit"
      @cancel="resetRefundForm"
    >
      <div v-if="selectedBooking?.pre_checkin" class="mb-3 p-3 bg-light rounded">
        <small class="text-muted">
          <strong>Offer Applied:</strong>
          {{ selectedBooking.pre_checkin.offer?.name || "N/A" }}<br />
          <strong>Discount Amount:</strong> ₹{{
            selectedBooking.pre_checkin.precheckin_discount_amount || 0
          }}<br />
          <strong>Final Price:</strong> ₹{{
            selectedBooking.pre_checkin.final_price || 0
          }}
        </small>
      </div>
      <b-form>
        <b-form-group label="Refund Amount">
          <b-form-input
            v-model="refundForm.refund_amount"
            type="number"
            step="0.01"
            :max="selectedBooking?.pre_checkin?.precheckin_discount_amount"
            required
          ></b-form-input>
          <small class="text-muted"
            >Maximum refund amount: ₹{{
              selectedBooking?.pre_checkin?.precheckin_discount_amount || 0
            }}</small
          >
        </b-form-group>

        <b-form-group label="Refund Method">
          <b-form-select
            v-model="refundForm.refund_method"
            :options="refundMethodOptions"
            required
          ></b-form-select>
        </b-form-group>

        <b-form-group label="Refund Date">
          <b-form-input
            v-model="refundForm.refund_date"
            type="date"
            required
          ></b-form-input>
        </b-form-group>

        <b-form-group label="Refund Notes">
          <b-form-textarea
            v-model="refundForm.refund_notes"
            rows="3"
            placeholder="Additional notes about the refund..."
          ></b-form-textarea>
        </b-form-group>

        <b-form-group label="Refund Reference (Optional)">
          <b-form-input
            v-model="refundForm.refund_reference"
            placeholder="Transaction ID, Receipt No, etc."
          ></b-form-input>
        </b-form-group>
      </b-form>
    </b-modal>

    <!-- Mark Refund Pending Modal -->
    <!-- <b-modal v-model="showPendingModal" title="Mark Refund as Pending" @ok="handlePendingSubmit"
            @cancel="resetPendingForm">
            <div v-if="selectedBooking?.pre_checkin" class="mb-3 p-3 bg-light rounded">
                <small class="text-muted">
                    <strong>Offer Applied:</strong> {{ selectedBooking.pre_checkin.offer?.name || 'N/A' }}<br>
                    <strong>Discount Amount:</strong> ₹{{ selectedBooking.pre_checkin.precheckin_discount_amount || 0
                    }}<br>
                    <strong>Final Price:</strong> ₹{{ selectedBooking.pre_checkin.final_price || 0 }}
                </small>
            </div>
            <b-form>
                <b-form-group label="Refund Notes">
                    <b-form-textarea v-model="pendingForm.refund_notes" rows="3"
                        placeholder="Notes about why refund is pending..."></b-form-textarea>
                </b-form-group>

                <b-form-group label="Estimated Refund Amount">
                    <b-form-input v-model="pendingForm.estimated_refund_amount" type="number" step="0.01"
                        placeholder="Estimated amount to be refunded"></b-form-input>
                    <small class="text-muted">This is an estimate and can be adjusted later when processing the actual
                        refund.</small>
                </b-form-group>
            </b-form>
        </b-modal> -->

    <!-- Cancel Offer Modal -->
    <b-modal
      v-model="showCancelModal"
      title="Cancel Pre-checkin Offer"
      @ok="handleCancelSubmit"
      @cancel="resetCancelForm"
    >
      <div v-if="selectedBooking?.pre_checkin" class="mb-3 p-3 bg-light rounded">
        <small class="text-muted">
          <strong>Offer Applied:</strong>
          {{ selectedBooking.pre_checkin.offer?.name || "N/A" }}<br />
          <strong>Discount Amount:</strong> ₹{{
            selectedBooking.pre_checkin.precheckin_discount_amount || 0
          }}<br />
          <strong>Final Price:</strong> ₹{{
            selectedBooking.pre_checkin.final_price || 0
          }}
        </small>
      </div>
      <b-form>
        <b-form-group label="Cancellation Reason">
          <b-form-textarea
            v-model="cancelForm.cancellation_reason"
            rows="3"
            required
            placeholder="Please provide a reason for cancelling this offer..."
          ></b-form-textarea>
        </b-form-group>
        <div class="alert alert-warning">
          <i class="fas fa-exclamation-triangle"></i>
          <strong>Warning:</strong> Cancelling this offer will remove the discount and
          update the booking total price.
        </div>
      </b-form>
    </b-modal>
    <!-- Cash Payment Modal -->
    <b-modal
      id="cashPaymentModal"
      v-model="showCashPaymentModal"
      title="💰 Booking Payment Summary"
      hide-footer
      size="lg"
    >
      <div class="p-3 rounded shadow-sm border bg-white">
        <h5 class="mb-3 text-primary">Booking ID: {{ selectedBooking?.id }}</h5>

        <!-- Payment Summary Table -->
        <b-table
          bordered
          small
          :items="paymentSummary"
          :fields="paymentFields"
          class="mb-3"
        >
          <template #cell(amount)="data">
            <strong
              :class="{
                'text-success': data.item.type === 'credit',
                'text-danger': data.item.type === 'debit',
              }"
            >
              {{ data.item.type === "debit" ? "-" : "" }}{{ data.item.amount | currency }}
            </strong>
          </template>
        </b-table>

        <!-- Final Payable -->
        <div class="alert alert-info py-2">
          <strong>💡 Final Payable:</strong> {{ finalPayable | currency }}
          <!-- <span v-if="refundAmount > 0" class="ml-2 text-success">
                        (Refund {{ refundAmount | currency }})
                    </span> -->
        </div>
      </div>

      <!-- Refund Section -->
      <b-form @submit.prevent="submitCashPayment" class="mt-3">
        <b-form-group label="Refund / Adjustment Amount" label-for="payment-amount">
          <b-form-input
            id="payment-amount"
            v-model="paymentForm.amount"
            type="number"
            step="0.01"
            min="0"
            required
            readonly
          ></b-form-input>
        </b-form-group>

        <!-- <b-form-group label="Notes" label-for="payment-notes">
                    <b-form-textarea id="payment-notes" v-model="paymentForm.notes"
                        placeholder="Reason for adjustment (optional)" rows="3"></b-form-textarea>
                </b-form-group> -->

        <div class="d-flex justify-content-end gap-2">
          <b-button variant="secondary" @click="showCashPaymentModal = false"
            >Cancel</b-button
          >
          <b-button variant="success" type="submit">Save Adjustment</b-button>
        </div>
      </b-form>
    </b-modal>
  </div>
</template>

<script>
import { request } from "@/Util/Request";
import CrudTable from "@/components/CrudTable.vue";

export default {
  name: "UserList",
  components: {
    CrudTable,
  },

  data() {
    return {
      bookings: [],
      isLoading: false,
      showBookingModal: false,
      showPrecheckinModal: false,
      showCashPaymentModal: false,
      paymentForm: {
        amount: "",
        // notes: ''
      },
      paymentFields: [
        { key: "label", label: "Description" },
        { key: "amount", label: "Amount (₹)", class: "text-right" },
      ],
      selectedBooking: null,
      selectedPrecheckin: null,
      currentPage: 1,
      perPage: 10,
      sortBy: "created_at",
      sortDesc: true,
      roomSummary: "",
      roomChangeBooking: null,
      showRoomChangeModal: false,
      roomToChange: null,
      roomTypeToChange: null,
      availableRooms: [],
      selectedNewRoomId: null,
      roomChangeReason: "",
      isLoadingAvailableRooms: false,
      filters: {
        search: "",
        role: null,
        status: null,
      },

      // Refund Management Data
      showRefundModal: false,
      // showPendingModal: false,
      showCancelModal: false,
      refundForm: {
        refund_amount: "",
        refund_method: "",
        refund_date: "",
        refund_notes: "",
        refund_reference: "",
      },
      pendingForm: {
        refund_notes: "",
        estimated_refund_amount: "",
      },
      cancelForm: {
        cancellation_reason: "",
      },
      refundMethodOptions: [
        { value: "bank_transfer", text: "Bank Transfer" },
        { value: "upi", text: "UPI" },
        { value: "cash", text: "Cash" },
        { value: "credit_note", text: "Credit Note" },
      ],
      fields: [
        {
          key: "guest_info",
          label: this.$t("booking.guest"),
          sortable: false,
        },
        {
          key: "hotel_info",
          label: this.$t("booking.hotel"),
          sortable: false,
        },
        {
          key: "check_in",
          label: this.$t("booking.dates"),
          sortable: true,
          sortDirection: "asc",
        },
        {
          key: "room_info",
          label: this.$t("booking.rooms"),
          sortable: false,
        },
        {
          key: "payment_info",
          label: this.$t("booking.payment"),
          sortable: false,
        },
        {
          key: "reservation_status",
          label: this.$t("booking.status"),
          sortable: true,
          sortDirection: "asc",
        },
        {
          key: "precheckin_sent",
          label: this.$t("pre_checkin.pre_status"),
          sortable: true,
          sortDirection: "asc",
        },
        {
          key: "actions",
          label: this.$t("common.actions"),
          sortable: false,
        },
      ],
      roleOptions: [],
      statusOptions: [
        { value: null, text: this.$t("user.status_all") },
        { value: "active", text: this.$t("user.status_active") },
        { value: "inactive", text: this.$t("user.status_inactive") },
      ],
    };
  },
  computed: {
    paymentSummary() {
      if (
        !this.selectedBooking?.billings ||
        !Array.isArray(this.selectedBooking.billings)
      ) {
        return [];
      }

      return this.selectedBooking.billings.map((item) => ({
        label: item.item_name,
        amount: parseFloat(item.total_amount) || 0,
        type: item.billing_type,
      }));
    },
    finalPayable() {
      if (!this.selectedBooking?.billings) return 0;

      let totalCredits = this.selectedBooking.billings
        .filter((b) => b.billing_type === "credit" && b.payment_status === "unpaid")
        .reduce((sum, b) => sum + (parseFloat(b.total_amount) || 0), 0);

      let totalDebits = this.selectedBooking.billings
        .filter((b) => b.billing_type === "debit" && b.payment_status === "unpaid")
        .reduce((sum, b) => sum + (parseFloat(b.total_amount) || 0), 0);

      return totalCredits - totalDebits;
    },

    refundAmount() {
      if (
        !this.selectedBooking?.billings ||
        !Array.isArray(this.selectedBooking.billings)
      ) {
        return 0;
      }
      console.log("Calculating refund amount for booking:", this.selectedBooking.id);

      // Sum of amounts already paid
      const paidAmount = this.selectedBooking.billings
        .filter((b) => b.payment_status === "paid")
        .reduce((sum, b) => sum + (parseFloat(b.total_amount) || 0), 0);

      return paidAmount > this.finalPayable ? paidAmount - this.finalPayable : 0;
    },
  },
  methods: {
    getNetBillingAmount(billingArray) {
      if (!billingArray || !billingArray.length) return 0;

      let creditTotal = 0;
      let debitTotal = 0;

      billingArray.forEach((bill) => {
        let amt = parseFloat(bill.amount) || 0;
        if (bill.billing_type === "credit") {
          creditTotal += amt;
        } else if (bill.billing_type === "debit") {
          debitTotal += amt;
        }
      });

      return creditTotal - debitTotal;
    },

    // Format currency nicely
    formatCurrency(value) {
      return Number(value).toLocaleString("en-IN", {
        style: "currency",
        currency: "INR",
      });
    },

    // Tooltip payment summary in table format
    getPaymentSummaryTooltip(billingArray) {
      if (!billingArray || !billingArray.length) return "No payment details";

      let rows = billingArray
        .map(
          (bill) => `
                <tr>
                    <td>${bill.item_name || "Unknown"}</td>
                    <td>${bill.billing_type || ""}</td>
                    <td style="text-align:right;">${this.formatCurrency(bill.amount)}</td>
                </tr>
            `
        )
        .join("");

      return `
                <table style="width:100%; border-collapse: collapse; font-size:12px;">
                    <thead>
                        <tr>
                            <th style="text-align:left;">Item</th>
                            <th style="text-align:left;">Type</th>
                            <th style="text-align:right;">Amount</th>
                        </tr>
                    </thead>
                    <tbody>
                        ${rows}
                        <tr style="font-weight:bold; border-top: 1px solid #ccc;">
                            <td colspan="2">Total</td>
                            <td style="text-align:right;">${this.formatCurrency(
                              this.getNetBillingAmount(billingArray)
                            )}</td>
                        </tr>
                    </tbody>
                </table>
            `;
    },

    openCashPaymentModal(booking) {
      this.selectedBooking = booking;
      // Auto-fill with final payable amount
      this.paymentForm.amount = this.finalPayable;
      this.showCashPaymentModal = true;
    },

    async markAsCheckedIn(booking) {
      try {
        this.isLoading = true;
        await request({
          method: "post",
          url: `bookings/${booking.id}/checked-in`,
        });
        this.$bvToast.toast("Booking marked as Checked In successfully.", {
          title: "Success",
          variant: "success",
          solid: true,
        });
        this.$refs["action-table"].refreshTableData();
      } catch (error) {
        console.error(error);
        this.$bvToast.toast("Failed to update Checked In status.", {
          title: "Error",
          variant: "danger",
          solid: true,
        });
      } finally {
        this.isLoading = false;
      }
    },

    async submitCashPayment() {
      if (!this.paymentForm.amount || this.paymentForm.amount <= 0) {
        alert("Please enter a valid payment amount.");
        return;
      }

      try {
        this.isLoading = true;
        await request({
          method: "post",
          url: `/bookings/${this.selectedBooking.id}/cash-payment`,
          data: {
            amount: this.paymentForm.amount,
            // notes: this.paymentForm.notes
          },
        });
        this.$bvToast.toast("Cash payment recorded successfully!", {
          title: "Success",
          variant: "success",
          solid: true,
        });
        this.showCashPaymentModal = false;

        // Refresh booking data
        this.$refs["action-table"].refreshTableData();
      } catch (error) {
        console.error(error);
        this.$bvToast.toast("Failed to add payment", {
          title: "Error",
          variant: "danger",
          solid: true,
        });
      } finally {
        this.isLoading = false;
      }
    },

    openRoomChangeModal(booking) {
      this.roomChangeBooking = booking;
      this.showRoomChangeModal = true;
      // Reset all room change state
      this.roomToChange = null;
      this.roomTypeToChange = null;
      this.selectedNewRoomId = null;
      this.roomChangeReason = "";
      this.availableRooms = [];
    },

    async startRoomChange(room, roomType) {
      this.roomToChange = room;
      this.roomTypeToChange = roomType;
      this.selectedNewRoomId = null;
      this.roomChangeReason = "";
      this.availableRooms = [];
      this.isLoadingAvailableRooms = true; // Start loader

      try {
        const response = await request({
          method: "get",
          url: `/rooms/available`,
          params: {
            room_type_id: roomType.room_type.id,
            check_in: this.roomChangeBooking.check_in,
            check_out: this.roomChangeBooking.check_out,
          },
        });
        this.availableRooms = response.rooms.map((room) => ({
          id: room.id,
          room_number: room.room_number,
          label: room.room_number,
          view: room.view,
          floor: room.floor,
          size_sqft: room.size_sqft,
          notes: room.notes,
          type: room.room_type?.name || "",
        }));
      } finally {
        this.isLoadingAvailableRooms = false; // Stop loader
      }
    },

    // Called when "Confirm Change" is clicked
    async submitRoomChange() {
      if (!this.selectedNewRoomId || !this.roomToChange) return;
      await request({
        method: "post",
        url: `/booking-room-type-rooms/${this.roomToChange.id}/upgrade`,
        data: {
          new_room_id: this.selectedNewRoomId,
          change_reason: this.roomChangeReason,
        },
      });
      this.$bvToast.toast("Room changed successfully!", {
        title: "Success",
        variant: "success",
      });
      // Close the modal
      this.showRoomChangeModal = false;
      // Reset modal state
      this.roomToChange = null;
      this.roomTypeToChange = null;
      this.selectedNewRoomId = null;
      this.roomChangeReason = "";
      this.availableRooms = [];
      this.$refs["action-table"].refreshTableData();
    },

    cancelRoomChange() {
      this.roomToChange = null;
      this.roomTypeToChange = null;
      this.selectedNewRoomId = null;
      this.roomChangeReason = "";
      this.availableRooms = [];
    },

    formatDate(dateString) {
      if (!dateString) return "";
      return new Date(dateString).toLocaleDateString();
    },

    formatDateTime(dateTimeString) {
      if (!dateTimeString) return "";
      return new Date(dateTimeString).toLocaleString();
    },

    viewBookingDetails(booking) {
      this.selectedBooking = booking;
      this.showBookingModal = true;
    },

    viewPrecheckinDetails(booking) {
      this.selectedPrecheckin = booking;

      const roomSummary = (booking.booking_room_types || [])
        .map((rt) => `${rt.num_of_rooms}x ${rt.room_type?.name || ""}`)
        .join(", ");
      this.roomSummary = roomSummary;
      this.showPrecheckinModal = true;
    },

    getPaymentStatusClass(status) {
      switch (status) {
        case "paid":
          return "bg-success text-white";
        case "pending":
          return "bg-warning text-dark";
        case "failed":
          return "bg-danger text-white";
        default:
          return "bg-secondary text-white";
      }
    },

    getReservationStatusClass(status) {
      switch (status) {
        case "booked":
          return "bg-primary text-white";
        case "checked_in":
          return "bg-success text-white";
        case "completed":
          return "bg-info text-white";
        case "cancelled":
          return "bg-danger text-white";
        case "no_show":
          return "bg-warning text-dark";
        default:
          return "bg-secondary text-white";
      }
    },

    getPrecheckinLabel(status) {
      switch (status) {
        case 0:
          return this.$t("pre_checkin.pending");
        case 1:
          return this.$t("pre_checkin.sent");
        case 2:
          return this.$t("pre_checkin.pre_checkedin");
        default:
          return this.$t("pre_checkin.unknown") || "Unknown";
      }
    },

    getPrecheckinBadgeClass(status) {
      switch (status) {
        case 0:
          return "bg-warning text-dark";
        case 1:
          return "bg-primary text-white";
        case 2:
          return "bg-success text-white";
        default:
          return "bg-secondary text-white";
      }
    },

    handleSearch(search) {
      this.filters.search = search;
      this.currentPage = 1;
    },

    handleFilter(filter) {
      this.filters = { ...this.filters, ...filter };
      this.currentPage = 1;
    },

    isSendPrecheckinEnabled(checkinDate) {
      if (!checkinDate) return false;

      // Expecting dd/mm/yyyy
      const parts = checkinDate.split("/");
      if (parts.length !== 3) return false;

      const [day, month, year] = parts.map(Number);
      if (!day || !month || !year) return false;

      // Create date at midnight to avoid timezone hour shifts
      const checkin = new Date(year, month - 1, day, 0, 0, 0);
      const now = new Date();

      const diffMs = checkin.getTime() - now.getTime();
      const seventyTwoHoursInMs = 72 * 60 * 60 * 1000;
      return diffMs > 0 && diffMs <= seventyTwoHoursInMs;
    },

    async confirmBooking(booking) {
      this.isLoading = true;
      try {
        await request({
          method: "post",
          url: `/bookings/${booking.id}/confirm`,
        });
        this.$bvToast.toast("Booking confirmed and PDF sent!", {
          title: "Success",
          variant: "success",
        });
        this.$refs["action-table"].refreshTableData();
      } catch (error) {
        this.$bvToast.toast("Failed to confirm booking.", {
          title: "Error",
          variant: "danger",
        });
      } finally {
        this.isLoading = false;
      }
    },

    goToPreCheckinChat(item) {
      const query = {
        booking_id: item.id,
      };
      if (item.pre_checkin) {
        query.pre_checkin_id = item.pre_checkin.id;
      }
      this.$router.push({
        path: "/admin/pre-check-in/chat",
        query,
      });
    },

    async sendPrecheckin(booking) {
      if (
        !this.isSendPrecheckinEnabled(booking.check_in) ||
        booking.precheckin_sent == 1
      ) {
        this.$bvToast.toast(this.$t("pre_checkin.cannot_send_precheckin"), {
          title: this.$t("pre_checkin.action_not_allowed"),
          variant: "warning",
        });
        return;
      }

      this.isLoading = true;

      try {
        await request({
          method: "post",
          url: `/bookings/${booking.id}/send-precheckin`,
        });
        this.showBookingModal = false;
        this.$bvToast.toast(this.$t("pre_checkin.send_precheckin_success"), {
          title: this.$t("common.success"),
          variant: "success",
        });
        this.$refs["action-table"].refreshTableData();
      } catch (error) {
        console.error("Error sending pre-checkin:", error);
        this.$bvToast.toast(this.$t("pre_checkin.send_failed_message"), {
          title: this.$t("common.error"),
          variant: "danger",
        });
      } finally {
        this.isLoading = false;
      }
    },

    async checkoutBooking(booking) {
      if (!confirm(this.$t("pre_checkin.checkout_confirmation", { id: booking.id }))) {
        return;
      }

      this.isLoading = true;
      try {
        await request({
          method: "post",
          url: `/bookings/${booking.id}/checkout`,
        });

        this.$bvToast.toast(this.$t("pre_checkin.checkout_success"), {
          title: this.$t("common.success"),
          variant: "success",
        });

        this.$refs["action-table"].refreshTableData();
      } catch (error) {
        console.error("Error checking out booking:", error);
        this.$bvToast.toast(this.$t("pre_checkin.checkout_error"), {
          title: this.$t("common.error"),
          variant: "danger",
        });
      } finally {
        this.isLoading = false;
      }
    },

    // Refund Management Methods
    canProcessRefund(booking) {
      return (
        booking.pre_checkin &&
        booking.pre_checkin.offer_id &&
        booking.pre_checkin.precheckin_discount_amount > 0 &&
        booking.pre_checkin.refund_status !== "processed" &&
        booking.pre_checkin.refund_status !== "cancelled"
      );
    },

    canCancelOffer(booking) {
      return (
        booking.pre_checkin &&
        booking.pre_checkin.offer_id &&
        booking.pre_checkin.precheckin_discount_amount > 0 &&
        booking.pre_checkin.refund_status !== "cancelled" &&
        booking.pre_checkin.refund_status !== "processed"
      );
    },

    processRefund(booking) {
      this.selectedBooking = booking;
      this.refundForm.refund_amount =
        booking.pre_checkin?.precheckin_discount_amount || 0;
      this.refundForm.refund_date = new Date().toISOString().split("T")[0];
      this.showRefundModal = true;
    },

    // markRefundPending(booking) {
    //     this.selectedBooking = booking;
    //     this.pendingForm.refund_notes = '';
    //     this.showPendingModal = true;
    // },

    cancelOffer(booking) {
      this.selectedBooking = booking;
      this.showCancelModal = true;
    },

    async handleRefundSubmit() {
      if (!this.refundForm.refund_amount || this.refundForm.refund_amount <= 0) {
        this.$bvToast.toast("Please enter a valid refund amount", {
          title: "Validation Error",
          variant: "warning",
        });
        return;
      }

      if (!this.refundForm.refund_method) {
        this.$bvToast.toast("Please select a refund method", {
          title: "Validation Error",
          variant: "warning",
        });
        return;
      }

      if (!this.refundForm.refund_date) {
        this.$bvToast.toast("Please select a refund date", {
          title: "Validation Error",
          variant: "warning",
        });
        return;
      }

      try {
        const response = await request({
          method: "post",
          url: `/pre-checkin-offers/${this.selectedBooking.pre_checkin.id}/process-refund`,
          data: this.refundForm,
        });

        if (response.success) {
          this.$bvToast.toast("Refund processed successfully", {
            title: "Success",
            variant: "success",
          });
          this.resetRefundForm();
          this.$refs["action-table"].refreshTableData();
        }
      } catch (error) {
        console.error("Error processing refund:", error);
        this.$bvToast.toast("Failed to process refund", {
          title: "Error",
          variant: "danger",
        });
      }
    },

    async handlePendingSubmit() {
      if (!this.pendingForm.refund_notes.trim()) {
        this.$bvToast.toast("Please provide refund notes", {
          title: "Validation Error",
          variant: "warning",
        });
        return;
      }

      try {
        const response = await request({
          method: "post",
          url: `/pre-checkin-offers/${this.selectedBooking.pre_checkin.id}/mark-refund-pending`,
          data: this.pendingForm,
        });

        if (response.success) {
          this.$bvToast.toast("Refund marked as pending", {
            title: "Success",
            variant: "success",
          });
          this.resetPendingForm();
          this.$refs["action-table"].refreshTableData();
        }
      } catch (error) {
        console.error("Error marking refund pending:", error);
        this.$bvToast.toast("Failed to mark refund as pending", {
          title: "Error",
          variant: "danger",
        });
      }
    },

    async handleCancelSubmit() {
      if (!this.cancelForm.cancellation_reason.trim()) {
        this.$bvToast.toast("Please provide a cancellation reason", {
          title: "Validation Error",
          variant: "warning",
        });
        return;
      }

      try {
        const response = await request({
          method: "post",
          url: `/pre-checkin-offers/${this.selectedBooking.pre_checkin.id}/cancel-offer`,
          data: this.cancelForm,
        });

        if (response.success) {
          this.$bvToast.toast("Offer cancelled successfully", {
            title: "Success",
            variant: "success",
          });
          this.resetCancelForm();
          this.$refs["action-table"].refreshTableData();
        }
      } catch (error) {
        console.error("Error cancelling offer:", error);
        this.$bvToast.toast("Failed to cancel offer", {
          title: "Error",
          variant: "danger",
        });
      }
    },

    resetRefundForm() {
      this.refundForm = {
        refund_amount: "",
        refund_method: "",
        refund_date: "",
        refund_notes: "",
        refund_reference: "",
      };
      this.selectedBooking = null;
      this.showRefundModal = false;
    },

    // resetPendingForm() {
    //     this.pendingForm = {
    //         refund_notes: '',
    //         estimated_refund_amount: ''
    //     };
    //     this.selectedBooking = null;
    //     this.showPendingModal = false;
    // },

    resetCancelForm() {
      this.cancelForm = {
        cancellation_reason: "",
      };
      this.selectedBooking = null;
      this.showCancelModal = false;
    },

    async generateCheckoutBill(booking) {
      const bookingId = booking.id;
      try {
        const response = await request({
          method: "get",
          url: `/bookings/${bookingId}/checkout-bill`,
        });

        this.$bvToast.toast(this.$t("billing.checkout_generated_successfully"), {
          title: this.$t("common.success"),
          variant: "success",
          solid: true,
        });
        this.$refs["action-table"].refreshTableData();
        if (response && response.pdf_url) {
          window.open(response.pdf_url, "_blank");
        }
      } catch (error) {
        console.error("Error generating checkout bill:", error);
        this.$bvToast.toast(this.$t("billing.failed_to_generate_checkout"), {
          title: this.$t("common.error"),
          variant: "danger",
          solid: true,
        });
      }
    },
  },
  filters: {
    currency(value) {
      if (!value) return "₹0.00";
      return "₹" + parseFloat(value).toFixed(2);
    },
  },
};
</script>

<style scoped>
.guest-info,
.hotel-info,
.dates-info,
.room-info,
.payment-info {
  line-height: 1.2;
}

.card {
  transition: box-shadow 0.2s;
}

.card:hover,
.card.border-primary {
  box-shadow: 0 0 0 2px #0d6efd33;
  border-color: #0d6efd !important;
}

.room-card {
  border-radius: 14px;
  min-width: 180px;
  max-width: 220px;
  margin: 0 auto;
  font-size: 0.95rem;
  box-shadow: 0 2px 8px #e3e3e3;
  transition: box-shadow 0.2s, border-color 0.2s;
}

.room-card:hover,
.room-card.border-primary {
  box-shadow: 0 0 0 2px #0d6efd33, 0 2px 8px #e3e3e3;
  border-color: #0d6efd !important;
}

.room-card .card-body {
  padding: 0.75rem 1rem;
}

.room-card .card-footer {
  padding: 0.25rem 0.5rem;
}

.room-card {
  border-radius: 14px;
  min-width: 180px;
  max-width: 220px;
  margin: 0 auto;
  font-size: 0.95rem;
  box-shadow: 0 2px 8px #e3e3e3;
  transition: box-shadow 0.2s, border-color 0.2s;
}

.room-card:hover,
.room-card.border-primary {
  box-shadow: 0 0 0 2px #0d6efd33, 0 2px 8px #e3e3e3;
  border-color: #0d6efd !important;
}

.room-card .card-body {
  padding: 0.75rem 1rem;
}

.room-card .card-footer {
  padding: 0.25rem 0.5rem;
}

.guest-name,
.hotel-name {
  font-weight: 600;
  color: #333;
}

.dates-info .check-in,
.dates-info .check-out {
  font-size: 0.9rem;
  margin-bottom: 2px;
}

.room-count {
  font-weight: 600;
  color: #007bff;
}

.total-price {
  font-weight: 600;
  color: #28a745;
  font-size: 1.1rem;
}

.section-title {
  color: #495057;
  border-bottom: 2px solid #e9ecef;
  padding-bottom: 0.5rem;
  margin-bottom: 1rem;
}

.section {
  background: #f8f9fa;
  padding: 1rem;
  border-radius: 0.375rem;
  border-left: 4px solid #007bff;
}

.room-type-section {
  background: white;
  padding: 1rem;
  border-radius: 0.375rem;
  border: 1px solid #dee2e6;
}

.room-type-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}

.room-item {
  background: #f8f9fa;
  border: 1px solid #dee2e6 !important;
}

.guests-list {
  margin-top: 0.5rem;
}

.guest-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.25rem;
  font-size: 0.9rem;
}

.signature-container {
  background: white;
  text-align: center;
}

.badge {
  font-size: 0.75rem;
  padding: 0.375rem 0.75rem;
}

.avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: #007bff;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
}
</style>
